import { useEffect, useRef, useState } from 'react'

import { updateRentalPortalApplicantsTableConfig } from '../../../../../api'
import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  ENTITY_LISTING_VIEW_TYPE,
} from '../../../../../constants/common'
import { RENTAL_APPLICANT_ENTITY } from '../../../../../constants/entity'
import { SUBMENU_DEFAULT_VALUE } from '../../../../../constants/route'
import { useLinkLikeClick } from '../../../../../hooks'
import { isListViewTypeBoard } from '../../../../../utils/listViewType'
import { getApplicantProfileLink } from '../../../../RentalPortalApplicant'
import { useAsyncTable, useTableRefresh } from '../../../../Table'
import {
  getApplicantsBoardRefreshResult,
  getApplicantsHasActiveFilters,
  getApplicantsListViewType,
} from '../../RentalPortalApplicants.utils'
import { useRentalPortalApplicantsBoard } from '../useRentalPortalApplicantsBoard'

export function useRentalPortalApplicantsTablePage() {
  const [isConfigOpen, setConfigOpen] = useState(false)

  const pageScrollWrapperRef = useRef(null)

  const asyncTable = useAsyncTable({
    containerRef: pageScrollWrapperRef,
    entity: RENTAL_APPLICANT_ENTITY,
    sub: SUBMENU_DEFAULT_VALUE,
    withoutSubMenu: true,
  })

  const {
    columns,
    filterValues,
    handleLoadInit,
    handleReload: handleReloadTable,
    handleSetFilterValues,
    handleSetView,
    isLoadedInit,
    isRefetching,
    items,
    ordering,
    preview,
    view,
  } = asyncTable

  const listViewType = getApplicantsListViewType(view)

  const isBoardRegime = isListViewTypeBoard(listViewType)

  const board = useRentalPortalApplicantsBoard({
    filterValues,
    isEnabled: isBoardRegime && isLoadedInit,
    ordering,
  })

  const { handleReloadBoard, handleResetBoard, isBoardRefetching } = board

  async function handleReload() {
    return isBoardRegime
      ? Promise.all([handleReloadTable(), handleReloadBoard()]).then(
          getApplicantsBoardRefreshResult
        )
      : handleReloadTable()
  }

  const { refreshButtonProps } = useTableRefresh({
    isRefetching: isRefetching || isBoardRefetching,
    onReload: handleReload,
  })

  useEffect(() => {
    handleLoadInit()
  }, [])

  async function handleApply(nextConfig) {
    return updateRentalPortalApplicantsTableConfig({
      columns,
      preview,
      view,
      ...nextConfig,
    })
  }

  function handleConfigOpen() {
    setConfigOpen(true)
  }

  function handleChangeViewType(nextViewType) {
    if (isListViewTypeBoard(nextViewType)) {
      handleReloadTable()
    } else {
      handleResetBoard()
    }

    handleSetView(nextViewType)

    handleApply({ view: nextViewType })
  }

  async function handleShowStageInTable(stage) {
    await handleSetFilterValues({ ...filterValues, stage: [stage] })

    handleChangeViewType(ENTITY_LISTING_VIEW_TYPE.table)
  }

  const { handleClick, handleMouseDown } = useLinkLikeClick()

  function handleClickApplicant(applicantId, event) {
    handleClick({ event, link: getApplicantProfileLink(applicantId) })
  }

  function handleMouseDownApplicant(applicantId, event) {
    handleMouseDown({ event, link: getApplicantProfileLink(applicantId) })
  }

  function handleClearFilters() {
    handleSetFilterValues(EMPTY_OBJECT)
  }

  return {
    ...asyncTable,
    ...board,
    applicants: items?.items ?? EMPTY_LIST,
    entity: RENTAL_APPLICANT_ENTITY,
    handleApply,
    handleChangeViewType,
    handleClearFilters,
    handleClickApplicant,
    handleConfigOpen,
    handleMouseDownApplicant,
    handleReload,
    handleShowStageInTable,
    hasActiveFilters: getApplicantsHasActiveFilters(filterValues),
    isBoardRegime,
    isConfigOpen,
    listViewType,
    pageScrollWrapperRef,
    refreshButtonProps,
    setConfigOpen,
  }
}
